import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  ShieldCheck,
  Landmark,
  CalendarClock,
  RefreshCw,
  Mail,
  Loader2,
  AlarmClock,
  Check,
  CheckCheck,
  Undo2,
  History,
  Clock,
  FileQuestion,
  Heart,
} from "lucide-react";
import { toast } from "sonner";
import { emailReminders, markReminderRead, snoozeReminder } from "../lib/api";
import { CalendarAddButton } from "./google/GoogleButtons";
import { SmsReminderToggle } from "./SmsReminderToggle";
import { PushReminderToggle } from "./PushReminderToggle";
import { RenewalEmailToggle } from "./RenewalEmailToggle";
import { getCategory } from "../lib/categories";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { tr } from "../lib/i18n";

const typeIcon = {
  warranty_expiration: ShieldCheck,
  insurance_renewal: RefreshCw,
  tax_deadline: Landmark,
  appointment: CalendarClock,
  subscription: RefreshCw,
  letter_opens: Heart,
  other: AlarmClock,
};

const openTarget = (r, onView) => (String(r.document_id).startsWith("letter:") ? window.location.assign("/planning?open=letters") : onView?.(r.document_id));

const whenMeta = (days) => {
  if (days < 0)
    return { label: `${Math.abs(days)}d overdue`, cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300" };
  if (days === 0)
    return { label: "Today", cls: "bg-primary/15 text-primary" };
  if (days <= 14)
    return { label: `in ${days}d`, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" };
  if (days <= 60)
    return { label: `in ${days}d`, cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300" };
  return { label: `in ${days}d`, cls: "bg-muted text-muted-foreground" };
};

export const RemindersPanel = ({ open, onClose, data, onView }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState("upcoming");
  const reminders = tab === "history" ? data?.history || [] : tab === "snoozed" ? data?.snoozed || [] : data?.reminders || [];
  const [snoozeFor, setSnoozeFor] = useState(null);
  const snooze = async (e, r, days) => {
    e.stopPropagation();
    try { const res = await snoozeReminder(r.key, days); setSnoozeFor(null); refresh(); toast.success(days > 0 ? `Snoozed until ${new Date(res.until).toLocaleDateString()}` : "Back in upcoming"); } catch { toast.error("Couldn't snooze"); }
  };
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["reminders"] });
  const mark = async (e, r, read) => {
    e.stopPropagation();
    try { await markReminderRead({ key: r.key, read }); refresh(); toast.success(read ? "Moved to history" : "Back in upcoming"); } catch { toast.error("Couldn't update"); }
  };
  const markAll = async () => {
    try { const res = await markReminderRead({ all: true }); refresh(); toast.success(`${res.count} reminder${res.count === 1 ? "" : "s"} marked as read`); } catch { toast.error("Couldn't update"); }
  };

  useEffect(() => {
    setEmail(localStorage.getItem("hv_reminder_email") || "");
  }, [open]);

  const sendEmail = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    localStorage.setItem("hv_reminder_email", email);
    setSending(true);
    try {
      const res = await emailReminders(email);
      toast.success(`Sent ${res.count} reminders to ${email}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        toast.error("Email isn't configured yet", {
          description: "Add a Resend API key to enable email reminders.",
        });
      } else if (status === 400) {
        toast.error("No upcoming reminders to send");
      } else {
        toast.error("Could not send reminder email");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="reminders-panel" className="w-full sm:max-w-md overflow-y-auto p-0">
        <div className="h-1.5 w-full bg-primary" />
        <div className="p-6">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif text-2xl flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" /> {tr("Reminders")}
            </SheetTitle>
            <SheetDescription>
              {tr("Dates the AI pulled from your documents — warranties, renewals, and deadlines.")}
            </SheetDescription>
          </SheetHeader>

          {/* Email digest */}
          <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <h4 className="text-xs uppercase tracking-wider font-mono text-primary mb-2.5">
              {tr("Email me my reminders")}
            </h4>
            <div className="flex items-center gap-2">
              <Input
                data-testid="reminder-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tr("you@example.com")}
                className="bg-card"
              />
              <Button
                data-testid="send-reminder-email-btn"
                onClick={sendEmail}
                disabled={sending}
                className="shrink-0 gap-1.5 bg-primary hover:bg-primary/90"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {tr("Send")}
              </Button>
            </div>
          </div>
          <SmsReminderToggle />
          <PushReminderToggle />
          <RenewalEmailToggle />

          {/* Upcoming / History */}
          <div className="mt-5 flex items-center gap-2" data-testid="reminders-tabs">
            <button type="button" data-testid="reminders-tab-upcoming" onClick={() => setTab("upcoming")} className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${tab === "upcoming" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>{tr("Upcoming ·")} {data?.reminders?.length || 0}</button>
            {(data?.snoozed_count || 0) > 0 && <button type="button" data-testid="reminders-tab-snoozed" onClick={() => setTab("snoozed")} className={`rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition-colors ${tab === "snoozed" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}><Clock className="h-3 w-3" /> {tr("Snoozed ·")} {data.snoozed_count}</button>}
            <button type="button" data-testid="reminders-tab-history" onClick={() => setTab("history")} className={`rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition-colors ${tab === "history" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}><History className="h-3 w-3" /> {tr("History ·")} {data?.history_count || 0}</button>
            {tab === "upcoming" && (data?.reminders?.length || 0) > 0 && <button type="button" data-testid="reminders-mark-all" onClick={markAll} className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> {tr("Mark all read")}</button>}
          </div>

          {/* List */}
          <div data-testid="reminders-list" className="mt-3 space-y-2.5">
            {reminders.length === 0 && (
              <div className="rounded-xl border border-dashed border-border py-10 text-center text-muted-foreground" data-testid="reminders-empty">
                <AlarmClock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                {tab === "history" ? "Nothing in history yet. Mark a reminder as read and it lands here." : tab === "snoozed" ? "Nothing snoozed." : "No upcoming reminders. They'll appear here as you add dated documents."}
              </div>
            )}
            {reminders.map((r, i) => {
              const Icon = r.type === "followup" ? FileQuestion : (typeIcon[r.type] || AlarmClock);
              const isFollowup = r.type === "followup";
              const cat = getCategory(r.category);
              const when = whenMeta(r.days_until);
              return (
                <div
                  key={r.key || `${r.document_id}-${i}`}
                  role="button"
                  tabIndex={0}
                  data-testid="reminder-item"
                  onClick={() => !isFollowup && openTarget(r, onView)}
                  onKeyDown={(e) => e.key === "Enter" && !isFollowup && openTarget(r, onView)}
                  className={`w-full text-left paper-card bg-card border border-border rounded-xl p-3.5 flex items-start gap-3 cursor-pointer ${r.read ? "opacity-80" : ""}`}
                >
                  <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${cat.tint}`}>
                    <Icon className="h-4 w-4" style={{ color: cat.iconColor }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{r.label}</span>
                      <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full shrink-0 ${when.cls}`}>
                        {when.label}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {tr(r.type_label)} · {r.document_title}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground mt-1">{r.date}{r.read && r.read_at ? <span className="font-sans"> {tr("· read")} {new Date(r.read_at).toLocaleDateString()}{r.read_by ? ` by ${r.read_by}` : ""}</span> : null}{r.snoozed_until ? <span className="font-sans"> {tr("· snoozed until")} {new Date(r.snoozed_until).toLocaleDateString()}</span> : null}</div>
                    {snoozeFor === r.key && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`reminder-snooze-menu-${r.key}`} onClick={(e) => e.stopPropagation()}>
                        <span className="text-[11px] text-muted-foreground">{tr("Snooze for")}</span>
                        {[[7, "1 week"], [30, "1 month"], [90, "3 months"]].map(([d, l]) => <button key={d} type="button" data-testid={`reminder-snooze-${d}-${r.key}`} onClick={(e) => snooze(e, r, d)} className="rounded-full border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary">{l}</button>)}
                        <button type="button" onClick={(e) => { e.stopPropagation(); setSnoozeFor(null); }} className="text-xs text-muted-foreground hover:text-foreground">{tr("Cancel")}</button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <CalendarAddButton reminder={r} />
                    {!r.read && (r.snoozed_until ? (
                      <button type="button" data-testid={`reminder-unsnooze-${r.key}`} title={tr("Un-snooze")} aria-label={tr("Un-snooze")} onClick={(e) => snooze(e, r, 0)} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Undo2 className="h-3.5 w-3.5" /></button>
                    ) : (
                      <button type="button" data-testid={`reminder-snooze-${r.key}`} title={tr("Snooze")} aria-label={tr("Snooze")} onClick={(e) => { e.stopPropagation(); setSnoozeFor(snoozeFor === r.key ? null : r.key); }} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Clock className="h-3.5 w-3.5" /></button>
                    ))}
                    {r.read ? (
                      <button type="button" data-testid={`reminder-unread-${r.key}`} title={tr("Move back to upcoming")} aria-label={tr("Move back to upcoming")} onClick={(e) => mark(e, r, false)} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Undo2 className="h-3.5 w-3.5" /></button>
                    ) : (
                      <button type="button" data-testid={`reminder-read-${r.key}`} title={tr("Mark as read")} aria-label={tr("Mark as read")} onClick={(e) => mark(e, r, true)} className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-primary hover:bg-primary/10"><Check className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
